import { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import _get from 'lodash/get';
import _find from 'lodash/find';
import { useQuery } from '@apollo/client';
import Typography from '@material-ui/core/Typography';
import RadioGroupField from '../forms/radio-group-field';
import { useAppContext } from '../../contexts/app';
import { myGroupsQuery } from '../../apollo/groups';

const EventFormStatus = ({ disabled, forceTouched, event }) => {
  const {
    values: { groupId, status },
    setFieldValue,
  } = useFormikContext();
  const { isLoggedIn } = useAppContext();

  const { data: groupsData } = useQuery(myGroupsQuery, {
    skip: !isLoggedIn,
  });
  const group = useMemo(() => {
    if (!groupId) {
      return null;
    }
    if (event && event.group && event.group.id === groupId) {
      return event.group;
    }
    if (!groupsData) {
      return null;
    }
    const groups = _get(groupsData, 'me.groups', []);
    return _find(groups, ({ id }) => id === groupId);
  }, [groupsData, groupId, event]);

  // if they're in a group that requires approval and this event hasn't already been published
  const groupApprovalRequired = useMemo(() => (
    group &&
    !group.permissions.createEventWithoutApproval &&
    (!event || event.status !== 'PUBLISHED')
  ), [group, event]);

  // if they loaded the page and approval is required
  // but form is set to PUBLISHED, set to GROUP_PENDING
  useEffect(() => {
    if (groupApprovalRequired && status === 'PUBLISHED') {
      setFieldValue('status', 'GROUP_PENDING');
    } else if (!groupApprovalRequired && status === 'GROUP_PENDING') {
      setFieldValue('status', 'PUBLISHED');
    }
  }, [groupApprovalRequired, status])

  const options = useMemo(() => {
    if (groupId && !group) {
      return [{
        label: 'Loading...',
        value: null,
      }];
    }
    const array = [
      {
        label: 'Draft',
        value: 'DRAFT',
      }
    ];
    if (groupApprovalRequired) {
      array.push({
        label: (
          <span>
            Published <strong>pending group approval</strong> ({group.name} requires events to be approved before publishing. This event will be published and notifications will be sent once approved by a group admin.)
          </span>
        ),
        value: 'GROUP_PENDING',
      });
    } else {
      array.push({
        label: 'Published',
        value: 'PUBLISHED',
      });
    }
    return array;
  }, [groupId, group, groupApprovalRequired]);

  return (
    <Fragment>
      <Typography variant="h2">Publish</Typography>
      <RadioGroupField
        name="status"
        label="Status"
        disabled={disabled}
        touched={forceTouched}
        options={options}
      />
    </Fragment>
  );
};

EventFormStatus.propTypes = {
  disabled: PropTypes.bool.isRequired,
  forceTouched: PropTypes.bool.isRequired,
  event: PropTypes.object,
};

export default EventFormStatus;
